// The chat client (03-startup-session.md, "Signed in"): loaded only after sign-in, never on the
// first screen. Makes the client on the account's sync and crypto databases, starts it, signs out.
import { ClientEvent, createClient, HttpApiEvent, IndexedDBStore, PendingEventOrdering, SyncState, type ICreateClientOpts, type MatrixClient } from 'matrix-js-sdk';
import { ALL_OF_DESYR, atMost, capped, clearStorageForSignOut, databasesLeft, databasesOf, leaveFor, signOutBy } from '../core/device';
import { keep as keepMail } from '../core/mail';
import { forgetPushAccount, stopPush } from '../push/push';
import { signOutOfCompany } from './admin';
import { server } from '../core/server';
import { signedOutOf } from '../core/worker';
import { fetchUntilLeaving, onLeave, signingOut, type Session } from '../core/session';
import { config } from '../core/config';
import { followOtherTabs, followToken, nameDevice, renewingFetch } from '../id/sessions';
import { followSignOut } from '../id/tabs';
import { defaultAccount, setDefault, storePrefix, switchTo, waiting } from '../accounts/store';
import { roomNames } from '../people/names';
import { onLanguage } from '../i18n';
import { stillWhile } from '../blocks/Bits';

export interface ClientEvents {
  onSync: (state: SyncState) => void;
  onLoggedOut: () => void;
  /** The client exists (before it starts). */
  onMade?: (client: MatrixClient) => void;
}

/** The secret-storage keys known in this tab (filled by unlocking and by setup; never stored). */
export const secretKeys = new Map<string, Uint8Array<ArrayBuffer>>();

/** Secret-storage keys live in memory for this tab only (19, Data); every circle's client asks here (38). */
export const cryptoCallbacks: ICreateClientOpts['cryptoCallbacks'] = {
  getSecretStorageKey: async ({ keys }) => {
    const id = Object.keys(keys).find((k) => secretKeys.has(k));
    return id ? [id, secretKeys.get(id)!] : null;
  },
  cacheSecretStorageKey: (id, _info, key) => void secretKeys.set(id, key),
};

const liveSyncs = new WeakMap<MatrixClient, Promise<void>>();
const lived = new WeakSet<MatrixClient>();
/** The first sync from the server has come (not only this device's copy); `whenLive` once it has. */
export const isLive = (client: MatrixClient) => lived.has(client);
export const whenLive = (client: MatrixClient) => liveSyncs.get(client) ?? Promise.resolve();

/** The first sync from the server rather than this device's copy (at most 5 s; offline it goes on). */
export const liveSync = (client: MatrixClient) =>
  Promise.race([liveSyncs.get(client) ?? Promise.resolve(), new Promise<void>((r) => setTimeout(r, 5000))]);

/** `check`: the session is checked before the first sync (a Try again's start, 03, v1.58.76). */
export async function startChat(session: Session, events: ClientEvents, check = false): Promise<MatrixClient> {
  // The account's own databases: the first account's are desyr_sync and desyr (90), further ones their own (33).
  const store = new IndexedDBStore({ indexedDB, localStorage, dbName: `${storePrefix()}_sync` });
  let made: MatrixClient | undefined;
  const client = createClient({
    baseUrl: session.base_url,
    accessToken: session.access_token,
    userId: session.user_id,
    deviceId: session.device_id,
    store,
    timelineSupport: true,
    verificationMethods: ['m.sas.v1'],
    cryptoCallbacks,
    // A session from desyr ID's sessions call: a refused token is renewed and the request sent once more (36, Behaviour 6.4).
    // Once a switch's reload has begun, nothing new is sent (Safari reports what the reload cuts off).
    fetchFn: fetchUntilLeaving(session.expires_at !== undefined && config().desyrId ? renewingFetch(config(), session.user_id) : undefined),
    // A direct chat is titled by the other person's name, never their chat ID (02).
    roomNameGenerator: roomNames(() => made),
  });
  made = client;
  events.onMade?.(client);
  if (session.expires_at !== undefined) {
    followToken((token) => client.setAccessToken(token));
    followOtherTabs(session.user_id);
  }
  onLeave(() => client.http.abort()); // a switch's reload: its requests end first, caught
  // Signed out in another tab: this client stops without telling the server (36, Behaviour 4.4.2, v1.58.63).
  followSignOut(() => client.stopClient());
  // Names made of people's names ("and N more") follow a language switch.
  onLanguage(() => client.getRooms().forEach((r) => r.recalculate()));
  client.on(HttpApiEvent.SessionLoggedOut, events.onLoggedOut);
  client.on(ClientEvent.Sync, (state) => events.onSync(state));
  liveSyncs.set(
    client,
    new Promise((resolve) => {
      const live = (state: SyncState, _: unknown, data?: { fromCache?: boolean }) => {
        if ((state === SyncState.Prepared || state === SyncState.Syncing) && !data?.fromCache) resolve(void (lived.add(client), client.off(ClientEvent.Sync, live)));
      };
      client.on(ClientEvent.Sync, live);
    }),
  );
  stillWhile(liveSync(client));
  // A device from desyr ID's sessions call is named after its first sync (04, Device names).
  if (session.expires_at !== undefined) void liveSyncs.get(client)!.then(() => nameDevice({ ...session, access_token: client.getAccessToken() ?? session.access_token }));
  // Try again's start asks whether the session still holds (at most 5 s): one that ended goes as the
  // session ends (the client reports it); no answer, the start goes on.
  if (check)
    await atMost(client.whoami(), 5000).catch((e: unknown) => {
      if (isLoggedOut(e)) throw e;
    });
  // What arrived since the last save is kept when the page goes away.
  addEventListener('pagehide', () => void store.save(true));
  await store.startup();
  // This tab holds the circle's crypto database while its client runs (38, Behaviour 2.2).
  void navigator.locks?.request(`desyr_crypto_${storePrefix()}`, () => new Promise(() => {}));
  await client.initRustCrypto({ cryptoDatabasePrefix: storePrefix() });
  // Thread replies stay out of the channel's timeline (10, rule 1); unsent messages wait apart (13).
  await client.startClient({ lazyLoadMembers: true, threadSupport: true, pendingEventOrdering: PendingEventOrdering.Detached });
  return client;
}

/** Once the app is open, never holding anything up: capabilities, media limits, sign-in details. */
export function readServerDetails(client: MatrixClient): void {
  client.getCapabilities().then((c) => (server.capabilities = c), () => {});
  client.getMediaConfig(server.features.authenticatedMedia).then((m) => (server.mediaConfig = m), () => {});
  client.getAuthMetadata().then((a) => (server.authMetadata = a), () => {});
}

/** The token was refused: the session has ended. */
export const isLoggedOut = (e: unknown) => ['M_UNKNOWN_TOKEN', 'M_MISSING_TOKEN'].includes((e as { errcode?: string })?.errcode ?? '');

/**
 * Signing out (03, "Signing out"): the server is told if it still listens (failures ignored), the
 * account's databases and local storage go (what 01 and 04 keep stays). The service worker forgets
 * the session first (06); push (28), the company's sign-in service (94) and the search index (29)
 * join here. Then the default or the first waiting account opens (33); with none left, push stops
 * and the sign-in page shows. This page's client, crypto store and databases are left as they are:
 * the window goes at once, and the next start deletes the databases before anything opens them
 * (taking them down in place could hang WebKit).
 */
/**
 * `after`: where the window goes once nothing is left (desyr ID's end-session address, 36, found out
 * meanwhile). `by`: the deadline from the press (Deadlines, v1.58.74): every request is capped by it.
 */
export async function signOut(client: MatrixClient, after: string | Promise<string> = import.meta.env.BASE_URL, by = signOutBy()): Promise<void> {
  signingOut(); // the ended token is neither renewed nor taken for "signed out elsewhere"
  const me = client.getUserId()!;
  signedOutOf(client.baseUrl); // the worker forgets this server's session first
  void forgetPushAccount(me); // and this account's push record (28, v1.58.79)
  const others = waiting().filter((a) => !a.signed_out);
  const next = others.find((a) => a.user_id === defaultAccount()) ?? others[0];
  // Told first, while the session still exists: push (the last account: nothing is pushed here
  // any more) and the company's sign-in service; each at most 5 s, failures ignored.
  await Promise.all([next ? undefined : capped(stopPush(), by), capped(signOutOfCompany(client.baseUrl, me, client.getAccessToken()), by)]);
  await capped(client.logout(false), by);
  client.http.abort(); // its sync ends here
  if (!next) {
    // Nothing of desyr's is left behind on this device (33, "Sign out of all").
    databasesLeft(ALL_OF_DESYR);
    clearStorageForSignOut();
    return leaveFor(await after);
  }
  databasesLeft(databasesOf(storePrefix(), me));
  keepMail(me, undefined);
  clearStorageForSignOut(['desyr_accounts', 'desyr_default_account', 'desyr_mail', 'desyr_push_client', 'desyr_circle_look']);
  if (defaultAccount() === me) setDefault(null);
  switchTo(next.user_id);
}

/** "Clear cached chats and reload": the synced data and the last pages go, the keys stay. */
export async function clearCachedChats(client: MatrixClient): Promise<void> {
  client.stopClient();
  try {
    localStorage.removeItem(`desyr_last_page:${client.getUserId()}`);
  } catch {
    // storage refused
  }
  await client.store.deleteAllData();
  location.reload();
}
